// import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Topic, InterviewCase } from "../pages/CaseSelectionPage";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";

const getCaseProblems = httpsCallable(functions, "getCaseProblems");

const fetchCases = async (ids?: number[]) => {
    try {
        const response = await getCaseProblems({ ids });
        const responseData = response.data as { data: BackendCase[] };
        const cases = responseData.data as BackendCase[];
        console.log("Fetched cases", cases);
        const frontendCases = cases.map((backendCase: BackendCase) =>
            transformCaseToInterviewCase(backendCase)
        );
        console.log("Transformed cases", frontendCases);
        return frontendCases;
    } catch (e) {
        console.error("Error fetching cases", e);
        return [];
    }
};
export function useFetchCases(ids?: number[]) {
    const query = useQuery<InterviewCase[]>({
        queryKey: ["cases", ids],
        queryFn: () => fetchCases(ids),
        enabled: Boolean(ids && ids.length >= 0),
        // Add caching configuration to prevent unnecessary refetches
        staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
        cacheTime: 30 * 60 * 1000, // Cache is kept for 30 minutes
        refetchOnWindowFocus: false, // Don't refetch when window regains focus
        refetchOnMount: false, // Don't refetch on component mount if data is cached
    });

    return {
        data: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}

// Suppose you have some enums/types in your backend:
export enum DifficultyLevel {
    EASY = "EASY",
    MEDIUM = "MEDIUM",
    HARD = "HARD",
}
export interface CaseContext {
    /** Some background/contextual info. Adjust to match your schema. */
    promptContext: string;
    promptQuestion: string;
}
export interface CaseQuestion {
    calculationFinalAnswer?: string;
    caseId: number;
    difficulty: string;
    expectedTime: string;
    question: string;
    questionId: number;
    type: "structure" | "brainstorm" | "math" | "recommendation";
    basicInformation?: Array<{
        content: string;
        id: number;
        graphUrl?: string;
        graphTitle?: string;
    }>;
    additionalInformation?: Array<{
        content: string;
        id: number;
    }>;
    keyPoints?: Array<{
        caseId: number;
        dataPoints: string[];
        id: number;
        questionId: number;
        title: string;
    }>;
}

// The "backend" interface
export interface BackendCase {
    id: number;
    title: string;
    difficulty: DifficultyLevel;
    tags: string[];
    companies: string[];
    industry: string; // or a specialized type
    caseType: string; // or a specialized type
    isInterviewerLed: boolean;
    expectedTime: string; // e.g., "30-45"
    wordBoosts: string[];
    caseContext: CaseContext;
    caseQuestions: CaseQuestion[];
}

// OPTIONAL: Create a default color or logic for each tag
function mapTagsToTopics(tags: string[]): Topic[] {
    return tags.map((tag) => ({
        name: tag,
        // You could apply some color logic if you have a known set of tags
        // or randomize, or just pick a single default color:
        color: "bg-blue-100 text-blue-700",
    }));
}
// OPTIONAL: Helper to convert backend difficulty -> frontend difficulty
function mapDifficulty(
    difficulty: DifficultyLevel
): InterviewCase["difficulty"] {
    switch (difficulty) {
        case DifficultyLevel.EASY:
            return "Easy";
        case DifficultyLevel.MEDIUM:
            return "Medium";
        case DifficultyLevel.HARD:
            return "Hard";
        default:
            // fallback if needed
            return "Easy";
    }
}

/**
 * Transform function to map a backend `Case` to a frontend `InterviewCase`.
 */
export function transformCaseToInterviewCase(
    backendCase: BackendCase
): InterviewCase {
    // Fix for handling different case formats in difficulty
    let difficultyValue: DifficultyLevel;

    if (typeof backendCase.difficulty === "string") {
        // Convert string difficulty to uppercase to match enum
        const uppercaseDifficulty = backendCase.difficulty.toUpperCase();
        difficultyValue = uppercaseDifficulty as DifficultyLevel;
    } else {
        difficultyValue = backendCase.difficulty;
    }

    return {
        id: backendCase.id,
        title: backendCase.title,
        // For the frontend, if there's more than one company, pick the first or join them
        company: backendCase.companies?.[0] || "N/A",
        industry: backendCase.industry,
        isInterviewerLed: backendCase.isInterviewerLed,
        expectedTime: backendCase.expectedTime,
        topics: mapTagsToTopics(backendCase.tags),
        // Map from your backend DifficultyLevel enum using our corrected value
        difficulty: mapDifficulty(difficultyValue),
        // mathFocus is optional; fill in logic if you have a specific way to determine
        mathFocus: backendCase.tags.includes("math-heavy") ? "High" : "Low",
        wordBoosts: backendCase.wordBoosts,
        caseType: backendCase.caseType,
        // The "context" might be the background in caseContext
        caseProblemContext: backendCase.caseContext?.promptContext || "",
        // The "caseProblem" might come from the first question in caseQuestions
        caseProblem: backendCase.caseContext?.promptQuestion || "",
    };
}
